import { Link, Navigate, useParams } from "react-router-dom";
import PageMeta from "@/components/seo/PageMeta";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import CollectionCard from "@/components/base/CollectionCard";
import { categories, SITE_URL } from "@/mocks/site";
import {
  formatDate,
  getCategory,
  getCollectionById,
  getRelated,
} from "@/lib/content";

export default function CollectionDetail() {
  const { id } = useParams<{ id: string }>();
  const collection = id ? getCollectionById(id) : undefined;

  if (!collection) {
    return <Navigate to="/collections" replace />;
  }

  const category = getCategory(collection.category);
  const related = getRelated(collection, 3);
  const pagePath = `/collections/${collection.id}`;
  const pageUrl = `${SITE_URL}${pagePath}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: collection.title,
    description: collection.summary,
    image: collection.cover,
    datePublished: collection.date,
    dateModified: collection.date,
    inLanguage: "zh-CN",
    articleSection: category?.name ?? "合集",
    keywords: collection.tags.join(","),
    author: {
      "@type": "Organization",
      name: collection.author,
    },
    publisher: {
      "@type": "Organization",
      name: "吃瓜片场",
      url: SITE_URL,
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": pageUrl,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "合集大厅",
        item: `${SITE_URL}/collections`,
      },
      ...(category
        ? [
            {
              "@type": "ListItem",
              position: 3,
              name: category.name,
              item: `${SITE_URL}/collections?cat=${category.slug}`,
            },
          ]
        : []),
      {
        "@type": "ListItem",
        position: category ? 4 : 3,
        name: collection.title,
        item: pageUrl,
      },
    ],
  };

  const crumbItems = [
    { label: "合集大厅", to: "/collections" },
    ...(category
      ? [{ label: category.name, to: `/collections?cat=${category.slug}` }]
      : []),
    { label: collection.title },
  ];

  return (
    <>
      <PageMeta
        title={`${collection.title} | 吃瓜片场`}
        description={collection.summary}
        keywords={collection.tags.join(",")}
        path={pagePath}
        ogType="article"
        image={collection.cover}
        jsonLd={[articleSchema, breadcrumbSchema]}
      />

      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10">
          <Breadcrumbs items={crumbItems} />
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <article className="lg:col-span-8">
            <div className="flex items-center flex-wrap gap-2.5">
              {category && (
                <Link
                  to={`/collections?cat=${category.slug}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-100 text-primary-800 text-xs font-medium hover:bg-primary-200 transition-colors"
                >
                  <i className={category.icon} aria-hidden="true" />
                  {category.name}
                </Link>
              )}
              <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                <i className="ri-book-marked-line" aria-hidden="true" />
                {collection.drama}
              </span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug mt-5">
              {collection.title}
            </h1>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" aria-hidden="true" />
                {collection.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true" />
                {formatDate(collection.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" aria-hidden="true" />
                {collection.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" aria-hidden="true" />
                {collection.views}
              </span>
            </div>

            <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden bg-background-200 mt-8">
              <img
                src={collection.cover}
                alt={`${collection.title} 合集配图`}
                title={`${collection.title} 私拍吃瓜合集`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mt-8 pl-4 border-l-2 border-primary-400">
              {collection.summary}
            </p>

            <section className="mt-10" aria-labelledby="detail-keypoints">
              <h2
                id="detail-keypoints"
                className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2"
              >
                <i className="ri-list-check-2 text-primary-600" aria-hidden="true" />
                看点速览
              </h2>
              <ul className="mt-5 flex flex-col gap-3">
                {collection.keyPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center shrink-0 mt-0.5 text-xs">
                      <i className="ri-check-line" aria-hidden="true" />
                    </span>
                    <span className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <div className="mt-12 flex flex-col gap-10">
              {collection.sections.map((section) => (
                <section key={section.heading}>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-snug">
                    {section.heading}
                  </h2>
                  <div className="flex flex-col gap-4 mt-4">
                    {section.body.map((paragraph) => (
                      <p
                        key={paragraph.slice(0, 18)}
                        className="text-base text-foreground-700 leading-loose"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-background-200">
              <span className="text-xs text-foreground-500 mr-1">相关标签</span>
              {collection.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 flex flex-col gap-6">
              <div className="bg-background-100 border border-background-200 rounded-lg p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-apps-2-line text-primary-600" aria-hidden="true" />
                  按分类浏览
                </h2>
                <ul className="flex flex-col gap-1">
                  {categories.map((cat) => (
                    <li key={cat.slug}>
                      <Link
                        to={`/collections?cat=${cat.slug}`}
                        className={`flex items-center justify-between px-3 py-2.5 rounded-md text-sm transition-colors ${
                          cat.slug === collection.category
                            ? "bg-primary-100 text-primary-800"
                            : "text-foreground-700 hover:bg-background-200"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <i className={cat.icon} aria-hidden="true" />
                          {cat.name}
                        </span>
                        <i className="ri-arrow-right-s-line" aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-secondary-950 text-secondary-100 rounded-lg p-5">
                <h2 className="font-heading text-base font-bold text-background-50 mb-3">
                  有现场线索？
                </h2>
                <p className="text-sm leading-relaxed text-secondary-200/85">
                  写清剧名、场景与大致时间点，我们会核对后整理成新的合集或补进已有合集。
                </p>
                <Link
                  to="/contact"
                  className="mt-5 flex items-center justify-center gap-1.5 w-full py-3 rounded-md bg-accent-500 text-secondary-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap"
                >
                  提交线索
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-16 pt-10 border-t border-background-200" aria-labelledby="detail-related">
          <h2
            id="detail-related"
            className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6"
          >
            相关推荐
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {related.map((item) => (
              <CollectionCard key={item.id} collection={item} />
            ))}
          </div>
        </section>
      </div>
    </>
  );
}